/**
 * Whether this browser session should send errors to Honeybadger.
 *
 * Local development and the headless e2e runs were reporting into the REAL
 * Honeybadger project — one CSP fault alone accumulated 889 notices from
 * `http://localhost:3001`, drowning genuine staging faults in noise.
 *
 * The cause is not an oversight in the plugin; it is `bin/launch-app`, which
 * sources the `cynque-webapp-staging` Heroku config vars into the local
 * environment so `npm run dev` can reach the real API. That hands a developer's
 * machine a live `NUXT_HONEYBADGER_API_KEY` *and* `NUXT_RELEASE_STAGE=staging`.
 *
 * That second value is what defeats Honeybadger's own guard. Its `reportData`
 * default (`null`) means "report unless `environment` is one of
 * `developmentEnvironments` — dev, development, test". A local session claiming
 * to be `staging` fails that test, so it reports, and its notices are filed
 * indistinguishably from the real staging app's.
 *
 * So the check is on where the code is RUNNING, not on what the environment
 * calls itself: a dev-server build, or any loopback host. Anything a user can
 * actually reach is a real deployment and keeps reporting.
 */
const LOCAL_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
])

export function shouldReportErrors({
  isDev,
  hostname,
}: {
  isDev: boolean
  hostname?: string
}): boolean {
  if (isDev) {
    return false
  }
  if (hostname && LOCAL_HOSTNAMES.has(hostname.toLowerCase())) {
    return false
  }
  // Default to REPORTING. An unrecognised or unavailable hostname is far more
  // likely to be a deployment we haven't thought of than a developer's laptop,
  // and silently losing production errors is the worse failure of the two.
  return true
}
